import React, { useEffect, useMemo, useState } from 'react';
import comuni from '../data/comuni-cap.json';
import './QuickAddressInput.css';

const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const titleCase = value => String(value || '').toLowerCase().replace(/(^|[\s'’-])([a-zà-ÿ])/g, (_, before, letter) => `${before}${letter.toUpperCase()}`);
const orderedCities = [...comuni].sort((a, b) => b.nome.length - a.nome.length);

export default function QuickAddressInput({ onApply, addressValue = '', className = '' }) {
  const [value, setValue] = useState(addressValue);
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => { setValue(addressValue || ''); }, [addressValue]);
  const cityQuery = useMemo(() => {
    const source = value.trim();
    if (!source) return '';
    if (source.includes(',')) return source.slice(source.lastIndexOf(',') + 1).trim();
    const afterNumber = /\d+[a-z]?\s+(.+)$/i.exec(source);
    return afterNumber?.[1] || source.split(/\s+/).pop() || '';
  }, [value]);
  const suggestions = useMemo(() => {
    const query = normalize(cityQuery);
    if (query.length < 2) return [];
    return comuni.filter(item => normalize(item.nome).startsWith(query)).slice(0, 6);
  }, [cityQuery]);

  const fill = (sourceValue = value) => {
    const source = String(sourceValue || '').trim();
    if (!source) return;
    const normalized = normalize(source).replace(/\s+/g, ' ');
    const city = orderedCities.find(item => {
      const name = normalize(item.nome).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const province = normalize(item.sigla || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(?:^|[,\\s])${name}(?:\\s*\\(?${province}\\)?)?\\s*$`, 'i').test(normalized);
    });
    if (!city) return setMessage('Città non riconosciuta: scrivila alla fine dell’indirizzo.');

    const cityAt = normalized.lastIndexOf(normalize(city.nome));
    let address = source.slice(0, cityAt).replace(/[\s,;-]+$/, '').trim();
    const explicitCap = address.match(/(?:^|[\s,])(\d{5})\s*$/);
    const cap = explicitCap?.[1] || (city.cap?.length === 1 ? city.cap[0] : '');
    if (explicitCap) address = address.slice(0, explicitCap.index).replace(/[\s,;-]+$/, '').trim();

    const cleanAddress = titleCase(address);
    setValue(cleanAddress);
    onApply?.({ indirizzo: cleanAddress, citta: city.nome, cap, provincia: city.provincia || city.sigla || '' });
    setOpen(false);
    setMessage(!cap && city.cap?.length > 1
      ? `Campi compilati. ${city.nome} ha più CAP: inserisci quello corretto.`
      : 'Campi compilati. Puoi correggerli prima di salvare.');
  };
  const chooseCity = city => {
    const source = value.trim();
    let prefix;
    if (source.includes(',')) prefix = source.slice(0, source.lastIndexOf(',')).replace(/[\s,;-]+$/, '');
    else {
      const match = /^(.*?\d+[a-z]?)\s+(.+)$/i.exec(source);
      prefix = match ? match[1] : source.slice(0, Math.max(0, source.length - cityQuery.length)).replace(/[\s,;-]+$/, '');
    }
    const completed = `${prefix}${prefix ? ', ' : ''}${city.nome}`;
    setValue(completed);
    fill(completed);
  };

  return <label className={`quick-address ${className}`.trim()}>
    <span>Indirizzo</span>
    <div><input aria-label="Indirizzo completo" placeholder="Esempio: Via Isonzo 7, Milano" value={value}
      autoComplete="off" onFocus={() => setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 140)}
      onChange={event => { const nextValue = event.target.value; setValue(nextValue); onApply?.({ indirizzo: nextValue }); setMessage(''); setOpen(true); }}
      onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); fill(); } }} />
      <button type="button" onClick={() => fill()}>Compila campi</button></div>
    {open && suggestions.length > 0 && <ul className="quick-address-suggestions">{suggestions.map(city => <li key={`${city.nome}-${city.sigla}`}>
      <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => chooseCity(city)}><strong>{city.nome}</strong><span>{city.provincia} ({city.sigla}) · CAP {city.cap?.join(', ')}</span></button>
    </li>)}</ul>}
    <small>{message || 'Scrivi via, numero e città. Se conosci il CAP puoi includerlo.'}</small>
  </label>;
}
